import React, { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import Button from '@material-ui/core/Button';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import Typography from '@material-ui/core/Typography';
import BorderColorIcon from '@material-ui/icons/BorderColor';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import CashFlowCard from './CashFlowCard';
import OrganizationalHealthCard from './OrganizationalHealthCard';
import PerformanceImprovementCard from './PerformanceImprovementCard';
import WorkOptimizationCard from './WorkOptimizationCard';
import ValueTypeSelection from './ValueTypeSelection';

import apiFetch from '../../utils/apiUtils';
import { DropdownContext } from '../../utils/constants';
import { ErrorContext } from '../../utils/constants';
import { getCurrentYear } from '../../utils/dateUtils';

const useStyles = makeStyles(() => ({
    valueSummaryHeader: {
        flexGrow: '1',
        fontSize: '24px',
        lineHeight: '28px',
    },
    valueSummarySubHeader: {
        fontSize: '14px',
        lineHeight: '16px',
        textAlign: 'right',
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end'
    }
}));

export default function ImpactList({ values, activeImpact, onClickImpact, refreshValues, disabled, onValuesChanged, targetDisabled}) {
    const classes = useStyles();
    const { dropdowns } = useContext(DropdownContext);
    const { triggerError } = useContext(ErrorContext);
    const { id: initiativeId } = useParams();
    const [ isEditMode, setEditMode ] = useState(false);
    const valueTypes = dropdowns['ValueType'] || {};
    const valueMetrics = dropdowns['ValueMetric'] || {};
    const priorityPractices = dropdowns['PriorityPractice'] || {};

    const getCollapsedHeader = (impact) => {
        switch(valueTypes[impact.valueTypeId]) {
            case 'Sap Project Validation':
                return impact.sapProjectNumber || impact.profitCentre || valueTypes[impact.valueTypeId];
            case 'Financial Metric':
            case 'Non-Financial Metric':
                return impact.metricId ? valueMetrics[impact.metricId] : valueTypes[impact.valueTypeId];
            case 'Organizational Health':
                return impact.priorityPracticeId ?
                    priorityPractices[impact.priorityPracticeId] :
                    valueTypes[impact.valueTypeId];
            default:
                return valueTypes[impact.valueTypeId];
        }
    };

    const getCollapsedSubheader = (impact) => {
        switch (valueTypes[impact.valueTypeId]) {
            case 'Sap Project Validation':
            case 'Financial Metric':
                let tag = 'cost savings'
                const metric = valueMetrics[impact.metricId];
                if ((metric && metric.substring(0, 19) === 'Implementation Cost') || impact.costElement) {
                    tag = 'implementation cost';
                }
                return `$${impact.summaryAmount} ${tag} in ${getCurrentYear()}`;
            default:
                return '';
        }
    };

    const updateImpact = (valueId, field, value, callback, errCallback) => {
        apiFetch(
            `/initiatives/${initiativeId}/values/${valueId}`,
            'PATCH',
            JSON.stringify({[field]: value}),
          () => {
                callback();
                onValuesChanged();
              },
            () => {
              if (errCallback) {
                errCallback();
              }
              triggerError({
                title: 'Update Error',
                description: `We were unable to update ${field} with value=${value}. Please try again.`,
                onRetry: () => updateImpact(valueId, field, value, callback)
              });
            }
        );
    };

    const updateMetric = (metricId, data) => {
        return apiFetch(
            `/initiatives/${initiativeId}/metricYears/${metricId}`,
            'PATCH',
            JSON.stringify(data),
            () => {onValuesChanged()},
            () => triggerError({
                title: 'Communication Error',
                description: 'We were unable to save your updates to the impact data. Please try again.',
                onRetry: () => updateMetric(metricId, data)
            })
        );
    }

    const createMetricYear = (valueId, year, onSuccess = ()=>{}) => {
        return apiFetch(
            `/initiatives/${initiativeId}/values/${valueId}/metricYear`,
            'POST',
            JSON.stringify({ year }),
            onSuccess,
            () => triggerError({
                title: 'Communication Error',
                description: 'We were unable to add another year to your metric. Please try again.',
                onRetry: () => createMetricYear(valueId, year, onSuccess)
            })
        );
    }

    const onClickEdit = (e) => {
        e.stopPropagation();
        setEditMode(true);
    }

    return (
        <div>
            {values.map((impact) => (
                <ExpansionPanel
                    expanded={impact.id === activeImpact}
                    key={impact.id}
                >
                    <ExpansionPanelSummary
                        onClick={() => { onClickImpact(impact.id); setEditMode(false); }}
                        expandIcon={<ExpandMoreIcon />}
                    >
                        {impact.id !== activeImpact && <Typography className={classes.valueSummaryHeader}>
                            {getCollapsedHeader(impact)}
                        </Typography>}
                        {impact.id !== activeImpact && <Typography className={classes.valueSummarySubHeader}>
                            {getCollapsedSubheader(impact)}
                        </Typography>}
                        {impact.id === activeImpact && <Typography className={classes.valueSummaryHeader}>
                            Metric # {impact.id}
                        </Typography>}
                        {!isEditMode && impact.id === activeImpact && !disabled && <Button onClick={onClickEdit}><BorderColorIcon/></Button>}
                    </ExpansionPanelSummary>
                    {!isEditMode && <ExpansionPanelDetails>
                        {valueTypes[impact.valueTypeId] === 'Sap Project Validation' &&
                        <CashFlowCard
                            impact={impact}
                            updateImpact={updateImpact}
                            refreshValues={refreshValues}
                            updateMetric={updateMetric}
                            createMetricYear={createMetricYear}
                            disabled={disabled}
                            targetDisabled={targetDisabled}
                        />}
                        {valueTypes[impact.valueTypeId] === 'Organizational Health' &&
                        <OrganizationalHealthCard
                            impact={impact}
                            updateImpact={updateImpact}
                            refreshValues={refreshValues}
                            disabled={disabled}
                        />}
                        {valueTypes[impact.valueTypeId] === 'Financial Metric' &&
                        <PerformanceImprovementCard
                            impact={impact}
                            updateImpact={updateImpact}
                            refreshValues={refreshValues}
                            updateMetric={updateMetric}
                            createMetricYear={createMetricYear}
                            disabled={disabled}
                            targetDisabled={targetDisabled}
                        />}
                        {valueTypes[impact.valueTypeId] === 'Non-Financial Metric' &&
                        <WorkOptimizationCard
                            impact={impact}
                            updateImpact={updateImpact}
                            refreshValues={refreshValues}
                            updateMetric={updateMetric}
                            createMetricYear={createMetricYear}
                            disabled={disabled}
                            targetDisabled={targetDisabled}
                        />}
                    </ExpansionPanelDetails>}
                    {isEditMode && <ExpansionPanelDetails>
                        <ValueTypeSelection
                            onCancel={()=> setEditMode(false)}
                            onSuccess={() => { refreshValues(); setEditMode(false); }}
                            impact={impact}
                            disabled={disabled}
                        />
                    </ExpansionPanelDetails>}
                </ExpansionPanel>
            ))}
        </div>
    );
}
